import React, { memo, useEffect, useState } from 'react';
import { Card, Typography } from '@mui/material';
import { cookieNames, Loader } from '@monorepo/common';
import cookies from 'js-cookie';

import { useDispatch } from 'react-redux';
import FinalBlockItem from './FinalBlockItem';
import { useClearDataQuery } from '../../../services/servicePostAJob';
import { IPaymentResponse } from '../../../services/servicePostAJob/responseInterfaces';
import { showDropButton } from '../../../features/sliceStepper';
import { book } from '../../../routers/books';
import {
  getEditJobPath,
  getOriginJobSite,
  getPublicJobUrl,
} from '../../../utils/jobAccountRoutes';
import { formatOrderAmount } from '../../../utils/order';
import { clearHeldBenefitClaim } from '../../../utils/benefitClaim';

interface IFinalBlockProps {
  data?: IPaymentResponse;
  siteId?: number | null;
}
const FinalBlock = ({ data, siteId }:IFinalBlockProps) => {
  const dispatch = useDispatch();
  // Read the draft guid at first render, before the effect below removes the cookie.
  // The query used to re-read the cookie itself when it built the URL, which lost
  // that race and cleared /Jobs/ClearTempData/undefined instead of the draft.
  const [tempJobGuid] = useState<string>(() => cookies.get(cookieNames.tempJob) || '');
  const { isLoading } = useClearDataQuery({ guid: tempJobGuid }, {
    skip: !tempJobGuid
  });

  useEffect(() => {
    dispatch(showDropButton());
  }, [isLoading]);

  useEffect(() => {
    if (data) {
      // The legacy window.gtag Google Ads conversion that fired here was removed
      // (D-24588): the parent PostJobListing already pushes the GA4 `purchase`
      // dataLayer event for this order, and the Ads conversion (AW-1019972986)
      // now fires from a tag inside GTM-K4WKF75L triggered by that event.
      cookies.remove(cookieNames.tempJob);
      // The claim is redeemed; drop the stored authority with the draft that owned it.
      clearHeldBenefitClaim();
    }
  }, [data]);

  const listingSiteId = data?.siteId === undefined ? siteId : data.siteId;
  const publicJobUrl = getPublicJobUrl(listingSiteId, data?.path);
  const originJobSite = getOriginJobSite(listingSiteId, data?.path);
  const editJobPath = data?.eguid
    ? getEditJobPath(listingSiteId, data.eguid)
    : null;
  // A fully covered (free) listing has no order, so there is no order amount to show.
  const amount = data?.orderId ? formatOrderAmount(data?.orderAmount) : null;

  return (
    <>
      <Loader isShow={isLoading} />

      <Card
        sx={{
          mt: 2,
          px: 2,
          p: 2.5
        }}
        variant="outlined"
        data-cy="finalStep"
      >

        <Typography
          variant="h4"
          fontWeight="bold"
        >
          {data?.orderId ? 'Order Complete - Thank You' : 'Job Posted - Thank You'}
        </Typography>
        <Typography
          variant="h5"
          marginTop={4}
          marginBottom={4}
        >
          Your job listing
        </Typography>

        <Typography
          variant="body1"
          marginTop={1}
          marginBottom={2}
        >
          Your job listing is now active.
        </Typography>

        {!data?.orderId ? (
          <Typography variant="body1" marginBottom={2}>
            No payment was charged for this posting.
          </Typography>
        ) : null}

        {data?.orderId ? (
          <Typography variant="body1" marginBottom={1}>
            <strong>Order ID:</strong>
            {' '}
            {data.orderId}
          </Typography>
        ) : null}

        {amount ? (
          <Typography variant="body1" marginBottom={3}>
            <strong>Amount:</strong>
            {' '}
            {amount}
          </Typography>
        ) : null}

        {publicJobUrl ? (
          <FinalBlockItem
            title="VIEW YOUR JOB LISTING"
            link={publicJobUrl}
          />
        ) : null}

        {originJobSite ? (
          <FinalBlockItem
            title={`ALSO VIEW ON ${originJobSite.name.toUpperCase()}`}
            link={originJobSite.url}
          />
        ) : null}

        {editJobPath ? (
          <FinalBlockItem
            isLocal
            title="EDIT YOUR JOB LISTING"
            link={editJobPath}
          />
        ) : null}

        {data?.orderGuid ? (
          <FinalBlockItem
            title="VIEW ORDER"
            link={`https://app.bevnet.com/orders/receipt/${data.orderGuid}`}
          />
        ) : null}

        <FinalBlockItem
          isLocal
          title="MANAGE JOB LISTINGS"
          link={book.jobsListings}
        />

        <Typography variant="body1" marginTop={4} color="#292929">
          <strong>IMPORTANT: </strong>
          {' '}
          {Number(data?.orderAmount || 0) > 0
            ? 'These links -- and a receipt for your purchase -- have also been sent to you via email.'
            : 'A confirmation with links to your job listing has also been sent to you via email.'}
        </Typography>

      </Card>
    </>
  );
};
export default memo(FinalBlock);
